import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";

const productAreas = [
  { icon: LanguageRoundedIcon, label: "Página web", detail: "Sitio publicado" },
  { icon: Inventory2OutlinedIcon, label: "Productos", detail: "Catálogo y stock" },
  { icon: EventAvailableRoundedIcon, label: "Agenda", detail: "Turnos y reservas" },
  { icon: PointOfSaleRoundedIcon, label: "Gestión", detail: "Ventas y operación" },
];

export default function HeroSection({
  onStartClick,
  onExploreClick,
  primaryLabel = "Crear mi página gratis",
  isAuthenticated = false,
  userName = "",
}) {
  return (
    <Box
      component="section"
      aria-labelledby="webprofit-home-title"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 10, sm: 12, md: 15 },
        pb: { xs: 5, sm: 7, md: 10 },
        background:
          "radial-gradient(circle at 85% 15%, rgba(243,90,58,.13), transparent 32%), linear-gradient(180deg, #FFF9F6 0%, #FFFFFF 74%)",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1.02fr) minmax(390px, .98fr)",
            },
            alignItems: "center",
            gap: { xs: 3.25, sm: 4.5, md: 7, lg: 9 },
          }}
        >
          <Box sx={{ maxWidth: 720 }}>
            <Chip
              icon={<CheckCircleRoundedIcon />}
              label={
                isAuthenticated
                  ? `Sesión activa${userName ? ` · ${userName}` : ""}`
                  : "Para comercios, servicios y profesionales"
              }
              sx={{
                mb: { xs: 1.5, sm: 2.25 },
                maxWidth: "100%",
                height: { xs: 32, sm: 36 },
                borderRadius: 999,
                bgcolor: "rgba(243,90,58,.1)",
                color: "#C83E21",
                fontWeight: 900,
                "& .MuiChip-icon": { color: "#F35A3A" },
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />

            <Typography
              id="webprofit-home-title"
              component="h1"
              sx={{
                color: "#19242F",
                fontWeight: 950,
                letterSpacing: "-.052em",
                lineHeight: 1,
                fontSize: {
                  xs: "2.3rem",
                  sm: "3.45rem",
                  md: "4.35rem",
                  lg: "4.8rem",
                },
              }}
            >
              Tu negocio online,
              <Box component="span" sx={{ display: "block", color: "#F35A3A" }}>
                listo para operar.
              </Box>
            </Typography>

            <Typography
              sx={{
                mt: { xs: 1.5, sm: 2.1, md: 2.75 },
                maxWidth: 650,
                color: "rgba(25,36,47,.7)",
                fontSize: { xs: ".94rem", sm: "1.06rem", md: "1.14rem" },
                lineHeight: { xs: 1.5, sm: 1.6, md: 1.65 },
                fontWeight: 550,
              }}
            >
              Con WebProfit publicás una página propia y seguís trabajando desde el mismo panel. Productos, ventas, turnos o reservas se activan según tu negocio.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.35}
              sx={{ mt: { xs: 2.35, sm: 3, md: 3.75 }, maxWidth: { xs: "100%", sm: 520 } }}
            >
              <Button
                onClick={onStartClick}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  minHeight: { xs: 50, sm: 56 },
                  px: { xs: 2.6, sm: 3.2 },
                  borderRadius: 999,
                  bgcolor: "#F35A3A",
                  textTransform: "none",
                  fontWeight: 900,
                  fontSize: "1rem",
                  boxShadow: "0 16px 34px rgba(243,90,58,.28)",
                  "&:hover": {
                    bgcolor: "#DF4728",
                    boxShadow: "0 18px 38px rgba(243,90,58,.32)",
                  },
                }}
              >
                {primaryLabel}
              </Button>
              <Button
                onClick={onExploreClick}
                variant="outlined"
                size="large"
                sx={{
                  minHeight: { xs: 48, sm: 56 },
                  px: { xs: 2.4, sm: 3 },
                  borderRadius: 999,
                  color: "#19242F",
                  borderColor: "rgba(25,36,47,.22)",
                  textTransform: "none",
                  fontWeight: 850,
                  "&:hover": {
                    borderColor: "#19242F",
                    bgcolor: "rgba(25,36,47,.035)",
                  },
                }}
              >
                Ver cómo funciona
              </Button>
            </Stack>

            <Stack
              direction="row"
              flexWrap="wrap"
              gap={{ xs: 1.1, sm: 2 }}
              sx={{ mt: { xs: 1.6, sm: 2.3 } }}
            >
              {["Sin tarjeta", "Configuración guiada"].map((item) => (
                <Stack key={item} direction="row" alignItems="center" spacing={0.65}>
                  <CheckCircleRoundedIcon sx={{ color: "#2A9D67", fontSize: 18 }} />
                  <Typography
                    sx={{
                      color: "rgba(25,36,47,.64)",
                      fontWeight: 750,
                      fontSize: ".88rem",
                    }}
                  >
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.45, sm: 2.3, md: 3 },
              borderRadius: { xs: 4, sm: 5, md: 6 },
              bgcolor: "#19242F",
              color: "#fff",
              boxShadow: "0 28px 70px rgba(25,36,47,.22)",
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: "#FF9F86",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: ".1em",
                    fontSize: ".72rem",
                  }}
                >
                  Ejemplo de proyecto
                </Typography>
                <Typography
                  sx={{
                    mt: 0.45,
                    fontWeight: 950,
                    fontSize: { xs: "1.45rem", sm: "1.7rem" },
                    letterSpacing: "-.03em",
                    lineHeight: 1.12,
                  }}
                >
                  Web y operación activas.
                </Typography>
              </Box>
              <Chip
                label="Online"
                size="small"
                sx={{
                  flexShrink: 0,
                  bgcolor: "rgba(42,157,103,.16)",
                  color: "#8DE0B7",
                  fontWeight: 900,
                }}
              />
            </Stack>

            <Paper
              elevation={0}
              sx={{
                mt: { xs: 1.5, sm: 2.5 },
                p: { xs: 1.25, sm: 2 },
                borderRadius: 4,
                bgcolor: "#fff",
                color: "#19242F",
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: "1rem" }}>
                    Mi negocio
                  </Typography>
                  <Typography
                    sx={{ mt: 0.35, color: "rgba(25,36,47,.58)", fontSize: ".84rem" }}
                  >
                    Web publicada · gestión activa
                  </Typography>
                </Box>
                <CheckCircleRoundedIcon sx={{ color: "#2A9D67", flexShrink: 0 }} />
              </Stack>

              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, 1fr)", md: "1fr 1fr" },
                  gap: { xs: .7, sm: 1 },
                }}
              >
                {productAreas.map(({ icon: Icon, label, detail }) => (
                  <Box
                    key={label}
                    sx={{
                      minWidth: 0,
                      p: { xs: .85, sm: 1.35 },
                      borderRadius: 3,
                      bgcolor: "#F6F8FA",
                      border: "1px solid rgba(25,36,47,.06)",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 29, sm: 34 },
                        height: { xs: 29, sm: 34 },
                        display: "grid",
                        placeItems: "center",
                        borderRadius: 2.2,
                        bgcolor: "rgba(243,90,58,.11)",
                        color: "#F35A3A",
                      }}
                    >
                      <Icon sx={{ fontSize: 19 }} />
                    </Box>
                    <Typography
                      sx={{ mt: { xs: .65, sm: 1 }, fontWeight: 900, fontSize: { xs: ".78rem", sm: ".88rem" }, lineHeight: 1.15 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        display: { xs: "none", sm: "block" },
                        color: "rgba(25,36,47,.56)",
                        fontSize: ".74rem",
                        lineHeight: 1.3,
                      }}
                    >
                      {detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

          </Paper>
        </Box>
      </Container>
    </Box>
  );
}
